import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { doc, getDoc, collection, getDocs, query, orderBy, documentId } from "firebase/firestore";
import { useCart } from "../../context/CartContext";
import { useNavigate } from "react-router-dom";
import { slugify } from "../../slugify";
import { CartIcon } from "../../components/ActionIcons";
import "./FrequentlyBoughtTogether.css";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { IMAGE_COMING_SOON } from "../../imageComingSoon";

// FIELD_PATTERNS for extracting part number / title from product
const FIELD_PATTERNS = {
  partNo: [/part.?no/i, /part.?number/i, /model.?no/i, /biba.?part/i],
  title: [/^product.?name$/i, /^tit+le$/i, /^name$/i],
};

const testKey = (key, patterns) => patterns.some((p) => p.test(key.trim()));

// Some products have both a "title" and a "Title" field (a data-entry
// artifact), and Firestore map-field key order isn't guaranteed stable
// across reads, so picking the first pattern match arbitrarily flips
// between the empty one and the real one on different loads. Prefer
// whichever match actually has a value over an empty one.
const matchField = (fieldsObj, patterns) => {
  const entries = Object.entries(fieldsObj || {}).filter(([key]) => testKey(key, patterns));
  if (entries.length === 0) return "";
  const nonEmpty = entries.find(([, value]) => value !== "" && value != null);
  return (nonEmpty || entries[0])[1] ?? "";
};

const VISIBLE_COUNT = 1;

const FrequentlyBoughtTogether = ({ frequentlyBoughtData = [] }) => {
  const [productsWithData, setProductsWithData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startIndex, setStartIndex] = useState(0);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  // Fetch product details including images, routes, and part numbers
  useEffect(() => {
    const fetchProductData = async () => {
      if (!frequentlyBoughtData || frequentlyBoughtData.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const productsWithAllData = await Promise.all(
          frequentlyBoughtData.map(async (item) => {
            try {
              // Fetch full product document to get image and part number
              const productRef = doc(
                db,
                "product_categories",
                item.categoryId,
                "subCategories",
                item.subCategoryId,
                "productData",
                item.productId
              );

              const productSnap = await getDoc(productRef);
              let image = IMAGE_COMING_SOON;
              let partNo = "";

              let title = "";
              if (productSnap.exists()) {
                const fields = productSnap.data().fields || {};

                // Get part number from fields
                partNo = matchField(fields, FIELD_PATTERNS.partNo) || item.productId;
                title = matchField(fields, FIELD_PATTERNS.title);

                // If imageUrl is in the main data, use it
                if (item.imageUrl) {
                  image = item.imageUrl;
                } else {
                  // Otherwise, Tixaru's own Front Image Url / Back Image Url
                  // only -- no other image-shaped field, same policy as the
                  // rest of the site's product images.
                  const imageField =
                    Object.entries(fields).find(
                      ([key, value]) => /front.*image/i.test(key) && typeof value === "string" && value
                    ) ||
                    Object.entries(fields).find(
                      ([key, value]) => /back.*image/i.test(key) && typeof value === "string" && value
                    );
                  image = imageField ? imageField[1] : IMAGE_COMING_SOON;
                }
              } else {
                // Product not found, use item's productId as part number
                partNo = item.productId;
              }

              return {
                ...item,
                image,
                partNo,
                title,
              };
            } catch (err) {
              console.error("Error fetching product:", item.productId, err);
              return {
                ...item,
                image: IMAGE_COMING_SOON,
                partNo: item.productId,
              };
            }
          })
        );

        // Now fetch category and subcategory names for routing
        const productsWithRoutes = await Promise.all(
          productsWithAllData.map(async (item) => {
            try {
              // Fetch category name
              const categoryRef = doc(db, "product_categories", item.categoryId);
              const categorySnap = await getDoc(categoryRef);
              const categoryName = categorySnap.exists()
                ? categorySnap.data().categoryName
                : "";

              // Fetch subcategory name
              const subCategoryRef = doc(
                db,
                "product_categories",
                item.categoryId,
                "subCategories",
                item.subCategoryId
              );
              const subCategorySnap = await getDoc(subCategoryRef);
              const subCategoryName = subCategorySnap.exists()
                ? subCategorySnap.data().subCategoryName
                : "";

              // Create route — URL slug is based on the product Title,
              // matching how TFTDetailPage resolves the product. Sibling
              // products must be walked in the same order (and given the
              // same duplicate-suffix treatment) as ProductListingPage /
              // TFTDetailPage, or a Title shared with another product in
              // the subcategory would produce the wrong slug.
              // Ordered explicitly by document ID — matches the same order
              // TFTDetailPage/ProductListingPage use for their own
              // productData fetches, so this component's computed slug
              // agrees with wherever it's actually routing to.
              const siblingsSnap = await getDocs(
                query(
                  collection(
                    db, "product_categories", item.categoryId,
                    "subCategories", item.subCategoryId, "productData"
                  ),
                  orderBy(documentId())
                )
              );
              const usedTitleSlugs = new Set();
              let titleSlug = slugify(item.title || subCategoryName);
              siblingsSnap.forEach((d) => {
                const fields = d.data().fields || {};
                const rowTitle = matchField(fields, FIELD_PATTERNS.title) || subCategoryName;

                let rowSlug = slugify(rowTitle);
                if (usedTitleSlugs.has(rowSlug)) {
                  rowSlug = `${rowSlug}-${d.id.slice(0, 6).toLowerCase()}`;
                }
                usedTitleSlugs.add(rowSlug);

                if (d.id === item.productId) titleSlug = rowSlug;
              });

              const categorySlug = slugify(categoryName);
              const subCategorySlug = slugify(subCategoryName);

              const route = `/tft/${categorySlug}/${subCategorySlug}/${titleSlug}`;

              return {
                ...item,
                route,
              };
            } catch (err) {
              console.error("Error fetching category info:", err);
              return {
                ...item,
                route: "#", // Fallback if routing fails
              };
            }
          })
        );

        setProductsWithData(productsWithRoutes);
        setStartIndex(0);
      } catch (err) {
        console.error("Error fetching frequently bought products:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [frequentlyBoughtData]);

  const maxStartIndex = Math.max(0, productsWithData.length - VISIBLE_COUNT);
  const canGoPrev = startIndex > 0;
  const canGoNext = startIndex < maxStartIndex;
  const visibleProducts = productsWithData.slice(startIndex, startIndex + VISIBLE_COUNT);

  const goPrev = () => setStartIndex((i) => Math.max(0, i - 1));
  const goNext = () => setStartIndex((i) => Math.min(maxStartIndex, i + 1));

  if (loading) {
    return (
      <div className="frequently-bought-sections">
        <h3 className="frequently-bought-title">Frequently bought together:</h3>
        <div className="frequently-bought-grid">
          <div className="frequently-bought-skeleton">Loading products...</div>
        </div>
      </div>
    );
  }

  if (!productsWithData || productsWithData.length === 0) {
    return null;
  }

  const showArrows = productsWithData.length > VISIBLE_COUNT;

  return (
    <div className="frequently-bought-sections">
      <h3 className="frequently-bought-title">Frequently bought together:</h3>
      <div className="frequently-bought-rows">
        {showArrows && (
          <button
            type="button"
            className="fbt-arrow fbt-arrow-prev"
            onClick={goPrev}
            disabled={!canGoPrev}
            aria-label="Show previous products"
          >
            <IoChevronBack size={20}/>
          </button>
        )}

        <div className="frequently-bought-grid">
          {visibleProducts.map((product, index) => {
            const displayName = product.title || product.label || "Product";
            return (
              <div
                key={startIndex + index}
                className="frequently-bought-card"
                onClick={() => {
                  if (product.route && product.route !== "#") {
                    navigate(product.route);
                  }
                }}
              >
                <div className="fbt-image-container">
                  <img
                    src={product.image}
                    alt={displayName}
                    className="fbt-image"
                    onError={(e) => {
                      e.currentTarget.src = IMAGE_COMING_SOON;
                    }}
                  />
                </div>

                <div className="fbt-info">
                  <h4 className="fbt-label" title={displayName}>{displayName}</h4>

                  {product.price && (
                    <div className="fbt-price">
                      ₹{parseFloat(product.price).toFixed(2)}
                    </div>
                  )}

                  {/* Add to Cart Button */}
                  <button
                    className="fbt-add-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      addToCart({
                        partNo: product.partNo || displayName,
                        title: displayName,
                        image: product.image,
                        quantity: 1,
                        price: parseFloat(product.price) || 0,
                      });
                    }}
                  >
                    <CartIcon size={12} />
                    Add
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {showArrows && (
          <button
            type="button"
            className="fbt-arrow fbt-arrow-next"
            onClick={goNext}
            disabled={!canGoNext}
            aria-label="Show more products"
          >
            <IoChevronForward size={20}/>
          </button>
        )}
      </div>
    </div>
  );
};

export default FrequentlyBoughtTogether;